import { gql } from "@apollo/client";
import { ORDER_FIELDS_FRAGMENT } from "./customFragments";

export const CREATE_RESERVATION = gql`
    mutation CreateReservation(
        $restaurantId: ID!
        $date: String!
        $time: AWSTime!
        $covers: Int!
        $status: ReservationStatus!
        $customerName: String!
        $customerEmail: String
        $customerPhone: String
        $notes: String
    ) {
        createReservation(
            input: {
                restaurantId: $restaurantId
                date: $date
                time: $time
                covers: $covers
                status: $status
                customerName: $customerName
                customerEmail: $customerEmail
                customerPhone: $customerPhone
                notes: $notes
            }
        ) {
            id
            restaurantId
            date
            time
            covers
            status
            customerName
            customerEmail
            customerPhone
            notes
        }
    }
`;

export const CREATE_EFTPOS_TRANSACTION_LOG = gql`
    mutation createEftposTransactionLog(
        $eftposProvider: EftposProvider!
        $transactionId: Int
        $merchantId: Int
        $amount: Int
        $type: String
        $payload: String!
        $restaurantId: ID!
        $timestamp: String
        $expiry: Int!
    ) {
        createEftposTransactionLog(
            input: {
                eftposProvider: $eftposProvider
                transactionId: $transactionId
                merchantId: $merchantId
                amount: $amount
                type: $type
                payload: $payload
                restaurantId: $restaurantId
                timestamp: $timestamp
                expiry: $expiry
            }
        ) {
            id
        }
    }
`;

export const UPDATE_REGISTER_KEY = gql`
    mutation UpdateRegister($id: ID!, $active: Boolean!) {
        updateRegister(input: { id: $id, active: $active }) {
            id
            active
        }
    }
`;

export const CREATE_ORDER = gql`
    mutation createOrder(
        $country: String
        $status: OrderStatus!
        $paid: Boolean!
        $paymentInProgress: Boolean
        $cashPayment: Boolean
        $onlineOrder: Boolean
        $guestCheckout: Boolean
        $covers: Int
        $stripePaymentId: String
        $orderScheduledAt: String
        $orderScheduledAtUtc: String
        $customerInformation: OrderCustomerInformationInput
        $type: OrderType!
        $number: String
        $table: String
        $notes: String
        $payments: [OrderPaymentInput]
        $paymentAmounts: OrderPaymentAmountsInput
        $thirdPartyDeliveryId: String
        $deliveryProvider: OnlineDeliveryProvider
        $deliveryAddress: String
        $deliveryNotes: String
        $deliveryDistanceMeters: Int
        $deliveryFeeDiscount: Int
        $deliveryFee: Int
        $deliveryTrackingUrl: String
        $uberDirectDeliveryPayload: String
        $total: Int!
        $surcharge: Int
        $discount: Int
        $promotionId: ID
        $promotionType: PromotionType
        $tax: Int
        $subTotal: Int!
        $registerId: ID!
        $products: [OrderProductInput!]
        $placedAt: String!
        $placedAtUtc: String!
        $completedAt: String
        $completedAtUtc: String
        $orderBatchable: Int
        $orderBatchWindowInSeconds: Int
        $orderUserId: ID!
        $orderRestaurantId: ID!
    ) {
        createOrder(
            input: {
                country: $country
                status: $status
                paid: $paid
                paymentInProgress: $paymentInProgress
                cashPayment: $cashPayment
                onlineOrder: $onlineOrder
                guestCheckout: $guestCheckout
                covers: $covers
                stripePaymentId: $stripePaymentId
                orderScheduledAt: $orderScheduledAt
                orderScheduledAtUtc: $orderScheduledAtUtc
                customerInformation: $customerInformation
                type: $type
                number: $number
                table: $table
                notes: $notes
                payments: $payments
                paymentAmounts: $paymentAmounts
                thirdPartyDeliveryId: $thirdPartyDeliveryId
                deliveryProvider: $deliveryProvider
                deliveryAddress: $deliveryAddress
                deliveryNotes: $deliveryNotes
                deliveryDistanceMeters: $deliveryDistanceMeters
                deliveryFeeDiscount: $deliveryFeeDiscount
                deliveryFee: $deliveryFee
                deliveryTrackingUrl: $deliveryTrackingUrl
                uberDirectDeliveryPayload: $uberDirectDeliveryPayload
                total: $total
                surcharge: $surcharge
                discount: $discount
                promotionId: $promotionId
                promotionType: $promotionType
                tax: $tax
                subTotal: $subTotal
                registerId: $registerId
                products: $products
                placedAt: $placedAt
                placedAtUtc: $placedAtUtc
                completedAt: $completedAt
                completedAtUtc: $completedAtUtc
                orderBatchable: $orderBatchable
                orderBatchWindowInSeconds: $orderBatchWindowInSeconds
                orderUserId: $orderUserId
                orderRestaurantId: $orderRestaurantId
            }
        ) {
            id
            orderRestaurantId
            placedAt
            number
            subTotal
            deliveryFee
        }
    }
`;

export const UPDATE_ORDER = gql`
    ${ORDER_FIELDS_FRAGMENT}
    mutation updateOrder(
        $orderId: ID!
        $country: String
        $status: OrderStatus
        $paid: Boolean
        $paymentInProgress: Boolean
        $cancellationReason: String
        $cashPayment: Boolean
        $onlineOrder: Boolean
        $guestCheckout: Boolean
        $covers: Int
        $stripePaymentId: String
        $orderScheduledAt: String
        $orderScheduledAtUtc: String
        $customerInformation: OrderCustomerInformationInput
        $type: OrderType
        $number: String
        $table: String
        $buzzer: String
        $notes: String
        $eftposReceipt: String
        $payments: [OrderPaymentInput]
        $paymentAmounts: OrderPaymentAmountsInput
        $thirdPartyDeliveryId: String
        $deliveryProvider: OnlineDeliveryProvider
        $deliveryAddress: String
        $deliveryNotes: String
        $deliveryDistanceMeters: Int
        $deliveryFeeDiscount: Int
        $deliveryFee: Int
        $deliveryTrackingUrl: String
        $total: Int
        $surcharge: Int
        $discount: Int
        $promotionId: ID
        $promotionType: PromotionType
        $tax: Int
        $subTotal: Int
        $registerId: ID
        $products: [OrderProductInput!]
        $placedAt: String
        $placedAtUtc: String
        $completedAt: String
        $completedAtUtc: String
        $parkedAt: String
        $parkedAtUtc: String
        $orderUserId: ID
        $orderRestaurantId: ID
    ) {
        updateOrder(
            input: {
                id: $orderId
                country: $country
                status: $status
                paid: $paid
                paymentInProgress: $paymentInProgress
                cancellationReason: $cancellationReason
                cashPayment: $cashPayment
                onlineOrder: $onlineOrder
                guestCheckout: $guestCheckout
                covers: $covers
                stripePaymentId: $stripePaymentId
                orderScheduledAt: $orderScheduledAt
                orderScheduledAtUtc: $orderScheduledAtUtc
                customerInformation: $customerInformation
                type: $type
                number: $number
                table: $table
                buzzer: $buzzer
                notes: $notes
                eftposReceipt: $eftposReceipt
                payments: $payments
                paymentAmounts: $paymentAmounts
                thirdPartyDeliveryId: $thirdPartyDeliveryId
                deliveryProvider: $deliveryProvider
                deliveryAddress: $deliveryAddress
                deliveryNotes: $deliveryNotes
                deliveryDistanceMeters: $deliveryDistanceMeters
                deliveryFeeDiscount: $deliveryFeeDiscount
                deliveryFee: $deliveryFee
                deliveryTrackingUrl: $deliveryTrackingUrl
                total: $total
                surcharge: $surcharge
                discount: $discount
                promotionId: $promotionId
                promotionType: $promotionType
                tax: $tax
                subTotal: $subTotal
                registerId: $registerId
                products: $products
                placedAt: $placedAt
                placedAtUtc: $placedAtUtc
                completedAt: $completedAt
                completedAtUtc: $completedAtUtc
                parkedAt: $parkedAt
                parkedAtUtc: $parkedAtUtc
                orderUserId: $orderUserId
                orderRestaurantId: $orderRestaurantId
            }
        ) {
            ...OrderFieldsFragment
        }
    }
`;

export const UPDATE_PRODUCT = gql`
    mutation UpdateProduct($id: ID!, $soldOut: Boolean, $soldOutDate: String, $totalQuantityAvailable: Int) {
        updateProduct(input: { id: $id, soldOut: $soldOut, soldOutDate: $soldOutDate, totalQuantityAvailable: $totalQuantityAvailable }) {
            id
        }
    }
`;

export const UPDATE_MODIFIER = gql`
    mutation UpdateModifier($id: ID!, $soldOut: Boolean, $soldOutDate: String, $totalQuantityAvailable: Int) {
        updateModifier(input: { id: $id, soldOut: $soldOut, soldOutDate: $soldOutDate, totalQuantityAvailable: $totalQuantityAvailable }) {
            id
        }
    }
`;

export const EMAIL_SALES_REPORTS = gql`
    mutation EmailSalesReports($restaurantId: String!, $emails: String!) {
        emailSalesReports(input: { restaurantId: $restaurantId, emails: $emails })
    }
`;

export const UPDATE_ORDER_STATUS = gql`
    mutation updateOrder(
        $orderId: ID!
        $status: OrderStatus!
        $placedAt: String!
        $paid: Boolean
        $completedAt: String
        $completedAtUtc: String
        $cancelledAt: String
        $cancelledAtUtc: String
        $refundedAt: String
        $refundedAtUtc: String
    ) {
        updateOrder(
            input: {
                id: $orderId
                status: $status
                placedAt: $placedAt
                paid: $paid
                completedAt: $completedAt
                completedAtUtc: $completedAtUtc
                cancelledAt: $cancelledAt
                cancelledAtUtc: $cancelledAtUtc
                refundedAt: $refundedAt
                refundedAtUtc: $refundedAtUtc
            }
        ) {
            id
        }
    }
`;

export const GET_ORDER_DELIVERY_STATUS = gql`
    query GetOrder($id: ID!) {
        getOrder(id: $id) {
            id
            type
            thirdPartyDeliveryId
            deliveryTrackingUrl
            uberDirectDeliveryError
        }
    }
`;

export const CREATE_URL_REDIRECT = gql`
    mutation CreateUrlRedirect($originalUrl: String!, $redirectUrl: String!, $restaurantId: ID) {
        createUrlRedirect(input: { originalUrl: $originalUrl, redirectUrl: $redirectUrl, restaurantId: $restaurantId }) {
            id
        }
    }
`;
